import React, { useState, useEffect, ReactNode } from 'react';
import {
  storeAuthData,
  getStoredToken,
  getStoredSsoToken,
  clearAuthData,
  getStoredUser,
  extractTokenFromUrl,
  isValidTokenFormat,
  decodeToken,
} from '@/utils/auth';
import { parseIframeParams, type IframeParams } from '@/lib/iframe-params';
import {
  setAuthTokenGetter,
  setUnauthenticatedHandler,
  resetSignInRedirect,
} from '@/lib/auth-token';
import { initiateOAuthLogin } from '@/utils/oauth';
import { AuthContext } from '@/contexts/auth-context';

interface OAuthAuthProviderProps {
  children: ReactNode;
}

export const OAuthAuthProvider: React.FC<OAuthAuthProviderProps> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [iframeParams, setIframeParams] = useState<IframeParams>({});
  const [ssoUserId, setSsoUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    setAuthTokenGetter(async () => getStoredSsoToken() || getStoredToken());
    setUnauthenticatedHandler(() => {
      initiateOAuthLogin();
    });
  }, []);

  const logout = () => {
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
    clearAuthData();
  };

  const loginWithOAuthCode = async (token: string, userData: any) => {
    try {
      setIsLoading(true);
      storeAuthData({ token, user: userData });
      setAuthToken(token);
      setUser(userData);
      setSsoUserId(userData.sso_user_id || userData.id);
      setUsername(userData.username);
      setIsAuthenticated(true);
      setAuthError(null);
      resetSignInRedirect();
      setIsLoading(false);
    } catch (error) {
      const nextError = error instanceof Error ? error : new Error('OAuth login failed');
      setAuthError(nextError);
      setIsLoading(false);
      throw error;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const params = parseIframeParams();
      setIframeParams(params);

      const urlToken = extractTokenFromUrl();
      if (urlToken && isValidTokenFormat(urlToken)) {
        const decoded = decodeToken(urlToken);
        if (decoded) {
          const ssoId = decoded.external_id || decoded.sso_user_id || decoded.user_id || '';
          const userData = {
            id: ssoId,
            sso_user_id: ssoId,
            username: decoded.username || '',
          };
          storeAuthData({ token: urlToken, user: userData });
          setAuthToken(urlToken);
          setUser(userData);
          setSsoUserId(ssoId);
          setUsername(userData.username);
          setIsAuthenticated(true);
          setAuthError(null);
          setIsLoading(false);
          return;
        }
      }

      const ssoToken = getStoredSsoToken();
      const legacyToken = getStoredToken();
      const storedToken = ssoToken || legacyToken;
      const storedUser = getStoredUser();

      if (storedToken && storedUser) {
        setAuthToken(storedToken);
        setUser(storedUser);
        setSsoUserId(storedUser.sso_user_id || storedUser.id);
        setUsername(storedUser.username);
        setIsAuthenticated(true);
        setAuthError(null);
        resetSignInRedirect();
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authToken,
        isAuthenticated,
        isLoading,
        authError,
        loginWithOAuthCode,
        logout,
        user,
        iframeParams,
        ssoUserId,
        username,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
